import React, { useState } from 'react';
import SportLogos from './SportLogos';

const SportScores = ({ sport, scores, totals, nflTotals }) => {
  const [expandedGames, setExpandedGames] = useState({});
  
  const sportColors = {
    MLB: { primary: 'text-blue-700', secondary: 'text-red-600' },
    NHL: { primary: 'text-blue-600', secondary: 'text-gray-800' },
    NBA: { primary: 'text-red-600', secondary: 'text-blue-700' },
    NFL: { primary: 'text-green-700', secondary: 'text-yellow-600' }
  };

  const colors = sportColors[sport];

  // Don't render anything if there are no events
  if (!scores?.events?.length) {
    return null;
  }

  return (
    <div className="mb-8">
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center mb-4 lg:mb-0">
            <img 
              src={SportLogos[sport]}
              alt={`${sport} logo`}
              className="h-8 w-8 sm:h-10 sm:w-10 mr-3 object-contain"
            />
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">{sport} Scores</h2>
          </div>
          <div className="grid grid-cols-3 gap-4 lg:flex lg:space-x-8">
            <div className="text-center">
              <p className="text-xs sm:text-sm text-gray-500">Home Teams</p>
              <p className={`text-2xl sm:text-3xl font-bold ${colors.primary}`}>{totals.home}</p>
            </div>
            <div className="text-center">
              <p className="text-xs sm:text-sm text-gray-500">Away Teams</p>
              <p className={`text-2xl sm:text-3xl font-bold ${colors.secondary}`}>{totals.away}</p>
            </div>
            <div className="text-center">
              <p className="text-xs sm:text-sm text-gray-500">Grand Total</p>
              <p className={`text-2xl sm:text-3xl font-bold black`}>{totals.away + totals.home}</p>
            </div>
          </div>
        </div>
        
        {/* NFL Grand Totals - Always Visible */}
        {sport === 'NFL' && nflTotals && (
          <div className="mt-4 border-t border-gray-200 pt-4">
            <div className="mb-3">
              <h3 className="text-lg font-semibold text-gray-900">NFL Scoring Totals</h3>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-md">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 sm:gap-4">
                {nflTotals.touchdowns > 0 && (
                  <div className="text-center">
                    <p className="text-xs sm:text-sm text-gray-500">Total Touchdowns</p>
                    <p className="text-lg sm:text-2xl font-bold text-green-700">{nflTotals.touchdowns}</p>
                  </div>
                )}
                {nflTotals.rushingTouchdowns > 0 && (
                  <div className="text-center">
                    <p className="text-xs sm:text-sm text-gray-500">Rushing TDs</p>
                    <p className="text-lg sm:text-2xl font-bold text-green-600">{nflTotals.rushingTouchdowns}</p>
                  </div>
                )}
                {nflTotals.receivingTouchdowns > 0 && (
                  <div className="text-center">
                    <p className="text-xs sm:text-sm text-gray-500">Receiving TDs</p>
                    <p className="text-lg sm:text-2xl font-bold text-green-500">{nflTotals.receivingTouchdowns}</p>
                  </div>
                )}
                {nflTotals.defensiveTouchdowns > 0 && (
                  <div className="text-center">
                    <p className="text-xs sm:text-sm text-gray-500">D/ST TDs</p>
                    <p className="text-lg sm:text-2xl font-bold text-green-800">{nflTotals.defensiveTouchdowns}</p>
                  </div>
                )}
                {nflTotals.fieldGoals > 0 && (
                  <div className="text-center">
                    <p className="text-xs sm:text-sm text-gray-500">Field Goals</p>
                    <p className="text-lg sm:text-2xl font-bold text-yellow-600">{nflTotals.fieldGoals}</p>
                  </div>
                )}
                {nflTotals.safeties > 0 && (
                  <div className="text-center">
                    <p className="text-xs sm:text-sm text-gray-500">Safeties</p>
                    <p className="text-lg sm:text-2xl font-bold text-red-600">{nflTotals.safeties}</p>
                  </div>
                )}
                {nflTotals.extraPoints > 0 && (
                  <div className="text-center">
                    <p className="text-xs sm:text-sm text-gray-500">Extra Points</p>
                    <p className="text-lg sm:text-2xl font-bold text-blue-600">{nflTotals.extraPoints}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {scores.events.map((event) => {
          const homeTeam = event.competitions[0].competitors.find(team => team.homeAway === 'home');
          const awayTeam = event.competitions[0].competitors.find(team => team.homeAway === 'away');
          
          return (
            <div key={event.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200">
              <div className="p-4">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center">
                    <img
                      src={awayTeam?.team?.logo}
                      alt={awayTeam?.team?.name}
                      className="w-6 h-6 sm:w-8 sm:h-8 mr-2"
                    />
                    <span className="font-semibold text-sm sm:text-base">{awayTeam?.team?.abbreviation}</span>
                  </div>
                  <span className={`text-xl sm:text-2xl font-bold ${colors.secondary}`}>{awayTeam?.score || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <img
                      src={homeTeam?.team?.logo}
                      alt={homeTeam?.team?.name}
                      className="w-6 h-6 sm:w-8 sm:h-8 mr-2"
                    />
                    <span className="font-semibold text-sm sm:text-base">{homeTeam?.team?.abbreviation}</span>
                  </div>
                  <span className={`text-xl sm:text-2xl font-bold ${colors.primary}`}>{homeTeam?.score || 0}</span>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-2 text-sm text-gray-500">
                {event.competitions[0]?.status?.type?.shortDetail || 'Status unavailable'}
              </div>
              {sport === 'MLB' && (() => {
                const pitchers = scores.gameSummaries?.[event.id]?.startingPitchers;
                if (!pitchers?.length) return null;
                const isPreGame = event.competitions[0]?.status?.type?.state === 'pre';
                const awayPitcher = pitchers.find(p => p.homeAway === 'away');
                const homePitcher = pitchers.find(p => p.homeAway === 'home');
                return (
                  <div className="bg-gray-50 px-4 py-2 text-sm border-t border-gray-100">
                    <div className="text-xs font-semibold text-gray-400 mb-1">Pitchers</div>
                    {awayPitcher && (
                      <div className="flex justify-between items-center text-gray-600">
                        <span>{awayPitcher.playerName}</span>
                        {!isPreGame && <span className="font-semibold">{awayPitcher.strikeouts} K</span>}
                      </div>
                    )}
                    {homePitcher && (
                      <div className="flex justify-between items-center text-gray-600">
                        <span>{homePitcher.playerName}</span>
                        {!isPreGame && <span className="font-semibold">{homePitcher.strikeouts} K</span>}
                      </div>
                    )}
                  </div>
                );
              })()}
              {sport === 'MLB' && scores.gameSummaries?.[event.id]?.homeRunHitters?.length > 0 && (
                <div className="bg-gray-100 px-4 py-2 text-sm">
                  <div className="font-semibold mb-1">Home Runs:</div>
                  {scores.gameSummaries[event.id].homeRunHitters.map((hr, index) => (
                    <div key={index} className="text-gray-600">
                      {hr.playerName} ({hr.teamAbbreviation})
                    </div>
                  ))}
                </div>
              )}
              {sport === 'NFL' && (
                <div className="border-t border-gray-200">
                  {/* Touchdown Scorers - Always Visible */}
                  {scores.gameSummaries?.[event.id]?.touchdownScorers?.length > 0 && (
                    <div className="px-4 py-3 bg-gray-50">
                      <div className="font-medium text-gray-700 mb-2">Touchdown Scorers:</div>
                      {scores.gameSummaries[event.id].touchdownScorers.map((td, index) => (
                        <div key={index} className="text-sm text-gray-600">
                          {td.playerName} ({td.teamAbbreviation})
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Scoring Stats Dropdown */}
                  <button
                    onClick={() => {
                      const newExpandedGames = { ...expandedGames };
                      newExpandedGames[event.id] = !newExpandedGames[event.id];
                      setExpandedGames(newExpandedGames);
                    }}
                    className="w-full px-4 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                  >
                    <div className="flex items-center justify-between">
                      <span>Scoring Breakdown</span>
                      <svg
                        className={`w-4 h-4 transform transition-transform ${expandedGames[event.id] ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  
                  {expandedGames[event.id] && (
                    <div className="px-4 pb-4 bg-gray-50">
                      {scores.gameSummaries?.[event.id]?.scoringStats && (
                        <div className="space-y-2">
                          {scores.gameSummaries[event.id].scoringStats.touchdowns > 0 && (
                            <div className="text-sm">
                              <span className="font-medium text-gray-700">Total Touchdowns:</span>
                              <span className="ml-2 text-gray-600">{scores.gameSummaries[event.id].scoringStats.touchdowns}</span>
                            </div>
                          )}
                          {scores.gameSummaries[event.id].scoringStats.rushingTouchdowns > 0 && (
                            <div className="text-sm">
                              <span className="font-medium text-gray-700">Rushing TDs:</span>
                              <span className="ml-2 text-gray-600">{scores.gameSummaries[event.id].scoringStats.rushingTouchdowns}</span>
                            </div>
                          )}
                          {scores.gameSummaries[event.id].scoringStats.receivingTouchdowns > 0 && (
                            <div className="text-sm">
                              <span className="font-medium text-gray-700">Receiving TDs:</span>
                              <span className="ml-2 text-gray-600">{scores.gameSummaries[event.id].scoringStats.receivingTouchdowns}</span>
                            </div>
                          )}
                          {scores.gameSummaries[event.id].scoringStats.defensiveTouchdowns > 0 && (
                            <div className="text-sm">
                              <span className="font-medium text-gray-700">D/ST TDs:</span>
                              <span className="ml-2 text-gray-600">{scores.gameSummaries[event.id].scoringStats.defensiveTouchdowns}</span>
                            </div>
                          )}
                          {scores.gameSummaries[event.id].scoringStats.fieldGoals > 0 && (
                            <div className="text-sm">
                              <span className="font-medium text-gray-700">Field Goals:</span>
                              <span className="ml-2 text-gray-600">{scores.gameSummaries[event.id].scoringStats.fieldGoals}</span>
                            </div>
                          )}
                          {scores.gameSummaries[event.id].scoringStats.safeties > 0 && (
                            <div className="text-sm">
                              <span className="font-medium text-gray-700">Safeties:</span>
                              <span className="ml-2 text-gray-600">{scores.gameSummaries[event.id].scoringStats.safeties}</span>
                            </div>
                          )}
                          {scores.gameSummaries[event.id].scoringStats.extraPoints > 0 && (
                            <div className="text-sm">
                              <span className="font-medium text-gray-700">Extra Points:</span>
                              <span className="ml-2 text-gray-600">{scores.gameSummaries[event.id].scoringStats.extraPoints}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SportScores; 